
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Transaction, PayoutRequest, TransactionStatus, UserTier, AffiliateStats, User, SystemSettings, Role, PACKAGES } from '../types';
import { db } from '../lib/database';
import { DEFAULT_SETTINGS } from '../constants';

interface SignalContextType {
  currentUser: User | null;
  isLoading: boolean;
  login: (email: string, pass: string) => Promise<{ success: boolean, message?: string }>;
  logout: () => void;
  register: (data: Partial<User>) => Promise<void>;
  recordClick: (tradingViewId: string) => void;
  refreshData: () => Promise<void>;
  
  transactions: Transaction[];
  payouts: PayoutRequest[];
  stats: AffiliateStats;
  chartData: any[];
  
  requestPayout: (amount: number, walletAddress: string) => void;
  approvePayout: (id: string) => void;
  rejectPayout: (id: string) => void;
  markPayoutPaid: (id: string) => void;
  
  addTransaction: (txn: Transaction) => Promise<void>;
  updateTransaction: (txn: Transaction) => Promise<void>;
  approveTransaction: (txnOrId: string | Transaction) => Promise<void>;
  rejectTransaction: (txnId: string) => Promise<void>;

  users: User[];
  systemSettings: SystemSettings;
  updateSettings: (newSettings: SystemSettings) => void;
  updateUser: (updatedUser: User) => void;
  deleteUser: (userId: string) => void;
  addUser: (newUser: User) => void;
  
  transferIncomeToReward: (amount: number) => Promise<void>;
  renewSubscription: (packageId: string, useRewardWallet: boolean) => Promise<{success: boolean, message: string}>;
  calculateGroupBonuses: (user: User) => number; // Exported for Admin View
}

const SignalContext = createContext<SignalContextType | undefined>(undefined);

export const useSignalData = () => {
  const context = useContext(SignalContext);
  if (context === undefined) throw new Error('useSignalData must be used within a SignalProvider');
  return context;
};

export const SignalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    const unsubscribeUsers = db.listenToUsers((data) => {
        setUsers(data);
        const sessionId = db.getSession();
        if (sessionId) {
            const found = data.find(u => u.id === sessionId);
            if (found) setCurrentUser(found);
        }
        setIsLoading(false);
    });
    const unsubscribeTxns = db.listenToTransactions((data) => setTransactions(data));
    const unsubscribePayouts = db.listenToPayouts((data) => setPayouts(data));
    const unsubscribeSettings = db.listenToSettings((data) => setSystemSettings(data));

    return () => {
        unsubscribeUsers();
        unsubscribeTxns();
        unsubscribePayouts();
        unsubscribeSettings();
    };
  }, []);

  const calculateTier = (months: number): UserTier => {
      if (months >= 6) return UserTier.LEVEL_3;
      if (months >= 3) return UserTier.LEVEL_2;
      return UserTier.LEVEL_1;
  };

  const getDirectCommission = (tier: UserTier) => {
      if (tier === UserTier.LEVEL_3) return systemSettings.commission?.t3_amount || 9;
      if (tier === UserTier.LEVEL_2) return systemSettings.commission?.t2_amount || 7.5;
      return systemSettings.commission?.t1_amount || 6;
  };

  const getIndirectCommission = (tier: UserTier) => {
      if (tier === UserTier.LEVEL_3) return systemSettings.commission?.k3_amount || 6;
      if (tier === UserTier.LEVEL_2) return systemSettings.commission?.k2_amount || 4.5;
      return systemSettings.commission?.k1_amount || 3;
  };

  const calculateGroupBonuses = (user: User) => {
    // Keep group bonus calculation as is unless specified otherwise
    if (!user.groupId) return 0;
    const groupMembers = users.filter(u => u.groupId === user.groupId);
    const memberCount = groupMembers.length;
    const fixedPart = 9 * memberCount;
    const groupTxns = transactions.filter(t => 
        t.status === TransactionStatus.APPROVED && 
        groupMembers.some(m => m.id === t.affiliateId)
    );
    const variablePart = 0.10 * groupTxns.reduce((sum, t) => sum + (t.commission + (t.indirectCommission || 0)), 0);
    const groupIncome = fixedPart + variablePart;
    let share = (0.70 * groupIncome) / memberCount;

    if (user.voteRank === 1) share += (0.40 * 0.30 * groupIncome);
    if (user.voteRank === 2) share += (0.30 * 0.30 * groupIncome);
    if (user.voteRank === 3) share += (0.30 * 0.30 * groupIncome);

    return parseFloat(share.toFixed(2));
  };

  const login = async (email: string, pass: string) => {
    // Chờ tối đa 10 giây để Firebase load users
    let attempts = 0;
    const maxAttempts = 20; // 20 x 500ms = 10 seconds
    
    while (users.length === 0 && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attempts++;
    }
    
    // Nếu sau 10 giây vẫn không có users, báo lỗi kết nối
    if (users.length === 0) {
        console.error('❌ Firebase không load được users sau 10 giây');
        return { 
          success: false, 
          message: "Không thể kết nối tới database. Vui lòng kiểm tra kết nối Firebase tại /db" 
        };
    }
    
    // Tìm user theo email hoặc phone
    const u = users.find(u => (u.email === email || u.phone === email) && u.password === pass);
    
    if (u) {
        // Cập nhật lastLogin
        const updatedUser = { ...u, lastLogin: new Date().toISOString() };
        await db.updateUserStats(u.id, { lastLogin: updatedUser.lastLogin });
        
        setCurrentUser(updatedUser);
        db.saveSession(u.id);
        return { success: true };
    }
    
    return { success: false, message: "Sai email hoặc mật khẩu." };
  };

  const register = async (data: Partial<User>) => {
    const id = `user_${Date.now()}`;
    const newUser: User = {
      id,
      name: data.name || '',
      email: data.email || '',
      phone: data.phone || '',
      telegramId: data.telegramId || '',
      tradingViewId: data.tradingViewId || '',
      role: Role.USER,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name || 'User')}&background=0F1623&color=fff`,
      status: 'PENDING',
      registerDate: new Date().toISOString(),
      registerIP: '127.0.0.1', 
      lastLogin: new Date().toISOString(),
      password: data.phone || '12345678', // Mật khẩu mặc định là số điện thoại
      referralCode: data.tradingViewId || Math.floor(100000 + Math.random() * 900000).toString(),
      referredBy: data.referredBy || '',
      groupId: 0,
      clickCount: 0,
      totalReferrals: 0,
      totalSales: 0,
      incomeWallet: 0,
      rewardWallet: 0,
      subscriptionExpiry: new Date().toISOString(),
      totalMonthsPaid: 0,
      currentTier: UserTier.LEVEL_1
    };
    
    await db.saveUser(newUser);
    
    const txn: Transaction = {
      id: `act_${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      customerName: newUser.name,
      customerId: newUser.id,
      product: 'Kích hoạt thành viên',
      amount: 100,
      commission: 0,
      status: TransactionStatus.PENDING,
      packageMonths: 1,
      affiliateId: newUser.referredBy 
    };
    await db.createTransaction(txn);
  };

  const approveTransaction = async (txnOrId: string | Transaction) => {
      let txn = typeof txnOrId === 'string' ? transactions.find(t => t.id === txnOrId) : txnOrId;
      if (!txn) return;

      const buyer = users.find(u => u.id === txn.customerId);
      if (buyer) {
          const currentExpiry = new Date(buyer.subscriptionExpiry).getTime() > Date.now() 
              ? new Date(buyer.subscriptionExpiry) : new Date();
          currentExpiry.setMonth(currentExpiry.getMonth() + txn.packageMonths);
          
          const newTotalMonthsPaid = (buyer.totalMonthsPaid || 0) + txn.packageMonths;
          const newTier = calculateTier(newTotalMonthsPaid);

          let gId = buyer.groupId;
          if (!gId) {
              for (let i = 1; i <= 8; i++) {
                  const count = users.filter(u => u.groupId === i).length;
                  if (count < 65) {
                      gId = i;
                      break;
                  }
              }
          }

          await db.updateUserStats(buyer.id, {
              status: 'ACTIVE',
              role: Role.AFFILIATE,
              subscriptionExpiry: currentExpiry.toISOString(),
              totalMonthsPaid: newTotalMonthsPaid,
              currentTier: newTier,
              groupId: gId || 1
          });
      }

      if (txn.affiliateId) {
          // Find direct referrer by TradingView ID or User ID
          const f1 = users.find(u => u.tradingViewId === txn.affiliateId || u.id === txn.affiliateId);
          if (f1) {
              const directCom = getDirectCommission(f1.currentTier);
              await db.updateUserStats(f1.id, {
                  incomeWallet: (f1.incomeWallet || 0) + directCom,
                  totalSales: (f1.totalSales || 0) + txn.amount
              });
              txn.commission = directCom;

              // Indirect commission (K) - only 1 level
              if (f1.referredBy) {
                  const f2 = users.find(u => u.tradingViewId === f1.referredBy || u.id === f1.referredBy);
                  if (f2) {
                      const indirectCom = getIndirectCommission(f2.currentTier);
                      await db.updateUserStats(f2.id, {
                          rewardWallet: (f2.rewardWallet || 0) + indirectCom
                      });
                      txn.indirectCommission = indirectCom;
                  }
              }
          }
      }

      txn.status = TransactionStatus.APPROVED;
      await db.createTransaction(txn);
  };

  const transferIncomeToReward = async (amount: number) => {
      if(!currentUser) return;
      
      const currentBonus = calculateGroupBonuses(currentUser);
      const totalAvailable = (currentUser.incomeWallet || 0) + currentBonus;

      if(totalAvailable < amount) {
          alert("Số dư khả dụng không đủ (bao gồm cả thưởng nhóm).");
          return;
      }

      const newIncome = (currentUser.incomeWallet || 0) - amount;
      const newReward = (currentUser.rewardWallet || 0) + amount;

      setCurrentUser({
          ...currentUser,
          incomeWallet: newIncome,
          rewardWallet: newReward
      });

      await db.updateUserStats(currentUser.id, {
          incomeWallet: newIncome,
          rewardWallet: newReward
      });
  };

  const renewSubscription = async (packageId: string, useReward: boolean) => {
      if(!currentUser) return { success: false, message: "Not logged in" };
      const pkg = PACKAGES.find(p => p.id === packageId);
      if(!pkg) return { success: false, message: "Package not found" };

      if(useReward) {
          if(currentUser.rewardWallet < pkg.price) return { success: false, message: "Ví thưởng không đủ" };
          const currentExpiry = new Date(currentUser.subscriptionExpiry).getTime() > Date.now() ? new Date(currentUser.subscriptionExpiry) : new Date();
          currentExpiry.setMonth(currentExpiry.getMonth() + pkg.months);
          const newTotal = (currentUser.totalMonthsPaid || 0) + pkg.months;

          await db.updateUserStats(currentUser.id, {
              rewardWallet: currentUser.rewardWallet - pkg.price,
              subscriptionExpiry: currentExpiry.toISOString(),
              totalMonthsPaid: newTotal,
              currentTier: calculateTier(newTotal)
          });
          return { success: true, message: "Gia hạn thành công!" };
      } else {
          const txn: Transaction = {
              id: `ren_${Date.now()}`,
              date: new Date().toISOString().split('T')[0],
              customerName: currentUser.name,
              customerId: currentUser.id,
              product: pkg.name,
              amount: pkg.price,
              commission: 0,
              affiliateId: currentUser.referredBy,
              status: TransactionStatus.PENDING,
              packageMonths: pkg.months
          };
          await db.createTransaction(txn);
          return { success: true, message: "Đơn hàng đã được tạo. Vui lòng liên hệ Admin để thanh toán." };
      }
  };

  return (
    <SignalContext.Provider value={{ 
      currentUser, isLoading, login, logout: () => { setCurrentUser(null); db.clearSession(); window.location.href = '/'; }, 
      register, recordClick: (c) => db.incrementUserClick(c),
      refreshData: async () => {},
      transactions, payouts, stats: {
          clicks: currentUser?.clickCount || 0,
          sales: transactions.filter(t => t.affiliateId === currentUser?.id).length,
          conversionRate: 0,
          totalRevenue: transactions.filter(t => t.affiliateId === currentUser?.id).reduce((s, t) => s + t.amount, 0),
          pendingCommission: 0,
          incomeBalance: (currentUser?.incomeWallet || 0) + (currentUser ? calculateGroupBonuses(currentUser) : 0),
          rewardBalance: currentUser?.rewardWallet || 0,
          paidCommission: payouts.filter(p => p.affiliateId === currentUser?.id && p.status === TransactionStatus.PAID).reduce((s, p) => s + p.amount, 0),
          currentTier: currentUser?.currentTier || UserTier.LEVEL_1,
          groupId: currentUser?.groupId || 0
      }, chartData: [],
      requestPayout: (a, w) => {
        if(!currentUser) return;
        const newPayout: PayoutRequest = {
          id: `pay_${Date.now()}`,
          affiliateName: currentUser.name,
          affiliateId: currentUser.id,
          date: new Date().toISOString().split('T')[0],
          amount: a,
          status: TransactionStatus.PENDING,
          walletAddress: w
        };
        db.savePayout(newPayout);
      }, 
      approvePayout: async (id) => {
        const p = payouts.find(x => x.id === id);
        if(p) db.savePayout({...p, status: TransactionStatus.APPROVED});
      }, 
      rejectPayout: async (id) => {
        const p = payouts.find(x => x.id === id);
        if(p) db.savePayout({...p, status: TransactionStatus.REJECTED});
      }, 
      markPayoutPaid: async (id) => {
        const p = payouts.find(x => x.id === id);
        if(p) db.savePayout({...p, status: TransactionStatus.PAID});
      },
      users, systemSettings, updateSettings: (s) => db.saveSettings(s), 
      updateUser: (u) => db.saveUser(u), deleteUser: (id) => db.deleteUser(id), addUser: (u) => db.saveUser(u),
      addTransaction: (t) => db.createTransaction(t), updateTransaction: (t) => db.createTransaction(t),
      approveTransaction, rejectTransaction: async (id) => {
        const t = transactions.find(x => x.id === id);
        if(t) db.createTransaction({...t, status: TransactionStatus.REJECTED});
      },
      transferIncomeToReward, renewSubscription, calculateGroupBonuses
    }}>
      {children}
    </SignalContext.Provider>
  );
};
